const _excluded = ["field", "value", "onChange", "label", "inline", "error", "errors", "isEditorComponent", "setActiveStyle", "setInactiveStyle"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'decap-cms-ui-next';
import { Map } from 'immutable';
import { basicSetup } from 'codemirror';
import { EditorView } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import { languages } from '@codemirror/language-data';
import { useEditorTheme } from './themes/theme';
import LanguageSelector from './LanguageSelector';
import { jsx as ___EmotionJSX } from "@emotion/react";
export default function CodeField(_ref) {
  let {
      field,
      value,
      onChange,
      label,
      inline,
      error,
      errors,
      isEditorComponent,
      setActiveStyle,
      setInactiveStyle
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const editorTheme = useEditorTheme();
  const keys = getFieldKeys();
  const [lang, setLang] = useState(getDefaultLanguage() || '');
  const langCompartment = useRef(new Compartment()).current;
  const themeCompartment = useRef(new Compartment()).current;
  const allowLanguageSelection = !field.has('allow_language_selection') || field.get('allow_language_selection');
  const languagesOptions = languages.map(lang => ({
    value: lang.name.toLowerCase(),
    label: lang.name,
    alias: lang.alias
  })).sort((a, b) => a.label.localeCompare(b.label));
  languagesOptions.unshift({
    value: 'plaintext',
    label: 'Plain Text'
  });
  function getDefaultLanguage() {
    const lang = valueIsMap() && value && value.get(keys.lang) || field.get('default_language') || 'plaintext';
    return lang;
  }
  function findLanguage(lang) {
    var _languages$find;
    if (!lang) return null;
    return (_languages$find = languages.find(l => {
      var _l$alias;
      return l.name.toLowerCase() === lang || ((_l$alias = l.alias) === null || _l$alias === void 0 ? void 0 : _l$alias.includes(lang));
    })) !== null && _languages$find !== void 0 ? _languages$find : null;
  }
  async function loadLanguage(lang) {
    if (!viewRef.current) return;
    if (lang === 'plaintext') {
      // Clear language highlighting
      viewRef.current.dispatch({
        effects: langCompartment.reconfigure([])
      });
      return;
    }
    const languageData = findLanguage(lang);
    if (!languageData) return;
    const languageSupport = await languageData.load();

    // Editor might have unmounted while loading
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: langCompartment.reconfigure(languageSupport)
    });
  }
  function handleChange(update) {
    if (update.docChanged) {
      const newCode = update.state.doc.toString();
      const newValue = writeCodeValue(newCode);
      onChange(newValue, {
        selection: update.state.selection.main
      });
    }
  }
  const updateListener = EditorView.updateListener.of(update => {
    handleChange(update);
  });

  // Initialize editor
  useEffect(() => {
    if (!editorRef.current) return;
    const doc = extractCodeValue();
    const state = EditorState.create({
      doc,
      extensions: [basicSetup, themeCompartment.of(editorTheme), updateListener, langCompartment.of([])]
    });
    const view = new EditorView({
      parent: editorRef.current,
      state
    });
    viewRef.current = view;
    if (isEditorComponent) {
      view.focus();
      setActiveStyle === null || setActiveStyle === void 0 || setActiveStyle();
    }
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  // Sync editor content when value changes externally
  useEffect(() => {
    if (!viewRef.current) return;
    const newDoc = extractCodeValue();
    const currentDoc = viewRef.current.state.doc.toString();
    if (newDoc !== currentDoc) {
      viewRef.current.dispatch({
        changes: {
          from: 0,
          to: viewRef.current.state.doc.length,
          insert: newDoc
        }
      });
    }
  }, [value, field, isEditorComponent]);

  // Lazy load language support when language changes
  useEffect(() => {
    loadLanguage(lang);
  }, [lang]);

  // Update theme when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: themeCompartment.reconfigure(editorTheme)
    });
  }, [editorTheme]);
  function getFieldKeys() {
    const defaults = {
      code: 'code',
      lang: 'lang'
    };
    if (isEditorComponent) return defaults;
    const keys = field.get('keys', Map()).toJS();
    return _objectSpread(_objectSpread({}, defaults), keys);
  }
  function valueIsMap() {
    return !field.get('output_code_only') || isEditorComponent;
  }
  function extractCodeValue() {
    var _value$get, _value$code;
    const {
      code
    } = getFieldKeys();
    if (!valueIsMap()) {
      return value !== null && value !== void 0 ? value : '';
    }
    // value could be Immutable Map or plain object
    return value !== null && value !== void 0 && value.get ? (_value$get = value.get(code)) !== null && _value$get !== void 0 ? _value$get : '' : (_value$code = value === null || value === void 0 ? void 0 : value[code]) !== null && _value$code !== void 0 ? _value$code : '';
  }
  function writeCodeValue(newCode) {
    if (!valueIsMap()) {
      return newCode;
    }
    const {
      code
    } = getFieldKeys();
    if (value !== null && value !== void 0 && value.set) {
      return value.set(code, newCode);
    }
    return _objectSpread(_objectSpread({}, value !== null && value !== void 0 ? value : {}), {}, {
      [code]: newCode
    });
  }
  function writeLangValue(newLang) {
    if (!valueIsMap()) {
      return value;
    }
    const {
      lang: langKey
    } = getFieldKeys();
    if (value !== null && value !== void 0 && value.set) {
      return value.set(langKey, newLang);
    }
    return _objectSpread(_objectSpread({}, value !== null && value !== void 0 ? value : {}), {}, {
      [langKey]: newLang
    });
  }
  return ___EmotionJSX(Field, {
    label: label,
    inline: inline,
    error: error,
    errors: errors
  }, allowLanguageSelection && ___EmotionJSX(LanguageSelector, {
    value: lang,
    options: languagesOptions,
    onChange: newLang => {
      setLang(newLang);
      const updatedValue = writeLangValue(newLang);
      onChange(updatedValue);
    }
  }), ___EmotionJSX("div", _extends({
    ref: editorRef,
    onFocus: () => setActiveStyle === null || setActiveStyle === void 0 ? void 0 : setActiveStyle(),
    onBlur: () => setInactiveStyle === null || setInactiveStyle === void 0 ? void 0 : setInactiveStyle()
  }, props)));
}